import React, { useEffect, useMemo, useState } from "react";
import styles from "./PracticeDetailsPage.module.scss";
import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import NonDentallyPracticeForm from "./NonDentallyPracticeForm";

type Provider = "dentally" | "non-dentally";
const PROVIDER: Provider = "dentally";

const STEPS: (Step & { path: string })[] = [
  { key: "practice", label: "Practice and clinician data", path: "/practiceDetails" },
  { key: "logo", label: "Logo Management", path: "/logoManagement" },
  { key: "social", label: "Social Media Links", path: "/socialLinks" },
  { key: "portfolio", label: "Portfolio Gallery", path: "/portfolioGallery" },
  { key: "consent", label: "Consent Templates", path: "/consent-templates" },
  { key: "preview", label: "Preview & Complete", path: "/preview" },
];

export default function PracticeDetailsPage({ provider = PROVIDER }: { provider?: Provider }) {
  const navigate = useNavigate();
  const isNonDentally = provider === "non-dentally";

  const [form, setForm] = useState({
    practiceName: "",
    practiceMobile: "",
    address: "",
    practiceEmail: "",
  });

  const [user, setUser] = useState<any>(null);
  const [practitioner, setPractitioner] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [showRoleWarningModal, setShowRoleWarningModal] = useState(false);

  useEffect(() => {
    if (isNonDentally) {
      setShowRoleWarningModal(false);
      setLoading(false);
      return;
    }

    const fetchAllData = async () => {
      setLoading(true);

      try {
        const [practiceRes, userRes, practitionerRes] = await Promise.allSettled([
          axios.get(`${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practice_data`),
          axios.get("/user"),
          axios.get(`${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practitioner_data`),
        ]);

        if (practiceRes.status === "fulfilled") {
          setForm({
            practiceName: practiceRes.value.data?.name || "",
            practiceMobile: practiceRes.value.data?.contact_no || "",
            address: practiceRes.value.data?.address || "",
            practiceEmail: practiceRes.value.data?.email || "",
          });
        } else {
          console.error("Error fetching practice data:", practiceRes.reason);
        }

        if (userRes.status === "fulfilled") {
          const { data } = userRes.value;
          let parsedUser = null;

          if (Array.isArray(data) && typeof data[1] === "object" && data[1] !== null) {
            parsedUser = data[1];
          } else if (data && typeof data === "object" && !Array.isArray(data)) {
            parsedUser = data;
          }

          if (parsedUser) {
            setUser(parsedUser);

            const normalizedRole = String(parsedUser.role || "")
              .trim()
              .toLowerCase();

            const isAllowedRole =
              normalizedRole.includes("clinician") ||
              normalizedRole.includes("hygienist");

            if (!isAllowedRole) {
              setShowRoleWarningModal(true);
            } else {
              setShowRoleWarningModal(false);
            }
          }
        } else {
          console.error("Error fetching user data:", userRes.reason);
        }

        if (practitionerRes.status === "fulfilled") {
          const practitionerData = practitionerRes.value.data;

          // keep null if empty object / empty response
          if (
            practitionerData &&
            typeof practitionerData === "object" &&
            Object.keys(practitionerData).length > 0
          ) {
            setPractitioner(practitionerData);
          } else {
            setPractitioner(null);
          }
        } else {
          console.error("Error fetching practitioner data:", practitionerRes.reason);
        }
      } catch (err) {
        console.error("Unexpected error fetching onboarding data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [isNonDentally]);

  const personalInfoRows = useMemo(() => {
    const userFullName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    const fullName = practitioner?.name || userFullName || "";
    const titleOrCredentials =
      practitioner?.role || user?.title || user?.role || "";
    const personalEmail = practitioner?.email || user?.email || "";
    const gdcNumber = practitioner?.gdc_number || "";
    const nhsNumber = practitioner?.nhs_number || "";

    return [
      { label: "Full name", value: fullName },
      { label: "Title / Credentials", value: titleOrCredentials },
      { label: "Personal email", value: personalEmail },
      { label: "GDC Number", value: gdcNumber },
      { label: "NHS Number", value: nhsNumber },
    ];
  }, [practitioner, user]);

  const handleNext = () => {
    navigate("/logoManagement");
  };

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main}>
        <SetupSteps
          steps={STEPS}
          current={1}
          rightLabel="0% Complete"
          onStepClick={isNonDentally ? undefined : (_, index) => navigate(STEPS[index].path)}
        />

        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <h1 className={styles.title}>Practice details</h1>
            <p className={styles.subtitle}>
              {isNonDentally
                ? "Enter your practice, sites and clinician details to set up your practice portal."
                : "To create a consent request, you’ll need both your patient’s details and your practice information. These details are pre-filled from DENTALLY, so just review and continue."}
            </p>
          </div>

          <div className={styles.progressCard}>
            <span className={styles.progressValue}>Step 1</span>
            <span className={styles.progressLabel}>Practice setup</span>
          </div>
        </section>

        {loading ? (
          <section className={styles.card}>
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner} />
              <p>Loading practice details...</p>
            </div>
          </section>
        ) : isNonDentally ? (
          <NonDentallyPracticeForm
            onComplete={handleNext}
            onBack={() => navigate(-1)}
          />
        ) : (
          <>
            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <div>
                  <h2 className={styles.cardTitle}>Practice information</h2>
                  <p className={styles.cardSubtitle}>
                    These details are pulled in automatically for review.
                  </p>
                </div>
              </div>

              <div className={styles.grid}>
                <div className={styles.fieldCard}>
                  <label className={styles.label}>Name</label>
                  <input className={styles.input} value={form.practiceName} disabled />
                </div>

                <div className={styles.fieldCard}>
                  <label className={styles.label}>Mobile</label>
                  <input className={styles.input} value={form.practiceMobile} disabled />
                </div>

                <div className={`${styles.fieldCard} ${styles.colSpanFull}`}>
                  <label className={styles.label}>Address</label>
                  <textarea
                    className={`${styles.input} ${styles.textarea}`}
                    rows={3}
                    value={form.address}
                    disabled
                  />
                </div>

                <div className={styles.fieldCard}>
                  <label className={styles.label}>Practice email</label>
                  <input className={styles.input} value={form.practiceEmail} disabled />
                </div>
              </div>
            </section>

            {user?.level === "user" && (
              <section className={styles.card}>
                <div className={styles.cardHeader}>
                  <div>
                    <h2 className={styles.cardTitle}>Clinician data</h2>
                    <p className={styles.cardSubtitle}>
                      Your practitioner details are shown below for confirmation.
                    </p>
                  </div>
                </div>

                <div className={styles.grid}>
                  {personalInfoRows.map((item) => (
                    <div key={item.label} className={styles.fieldCard}>
                      <label className={styles.label}>{item.label}</label>
                      <input className={styles.input} value={item.value} disabled />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {!isNonDentally && <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <div className={styles.nextActionWrap}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleNext}
              disabled={loading}
            >
              Next
            </button>
          </div>
        </div>}
      </main>

      {showRoleWarningModal && !loading && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowRoleWarningModal(false)}
        >
          <div
            className={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalIcon}>!</div>

            <h3 className={styles.modalTitle}>Warning</h3>

            <p className={styles.modalText}>
              You are not registered as a clinician in Dentally.
            </p>

            <p className={styles.modalSubtext}>
              Your current Dentally role is <strong>{user?.role || "Unknown"}</strong>.
            </p>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => setShowRoleWarningModal(false)}
              >
                Yes, proceed
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className={styles.footer}>
        Copyright © Medicube 2025. All Rights Reserved.
      </footer>
    </div>
  );
}
